import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { API_MODE } from '@/api'
import { ApiError } from '@/api/client'
import { useAdminAuth } from '@/context/AdminAuthContext'
import { useAuth } from '@/context/AuthContext'
import { Icon } from '@/components/icons'
import { Badge, Button, Field, Input } from '@/components/ui'

/**
 * 登录页。**一份表单，两个变体**：会员（默认）与后台（`AdminLogin`）。
 *
 * ### 为什么它**在 AppShell 之外**
 *
 * 侧栏 / 顶栏里的每一个入口都是「某账号的操作」（任务、作品、额度）。
 * 未登录时把这些摆出来，点下去全是 401，等于给用户一屏走不通的按钮。
 * 所以登录页是一张**独立的整屏**，没有侧栏也没有顶栏。
 *
 * ### 为什么是「一份表单 + 两个薄壳」而不是两个登录页（2026-10-07 拆表）
 *
 * 拆表之后**会员与后台是两套身份**：账号在两张表里、走两套登录端点
 * （`/api/auth/login` / `/api/admin/auth/login`）、token 存两个键。
 * 但它们**长得一样**：两个输入框、一颗提交键、一行报错、一段 mock 提示。
 *
 * 写两份的代价不是「多抄 60 行」，而是**下一处改动只会落到其中一份** ——
 * 比如加个「记住我」、改一句账号不存在的文案，改完只有一边生效，
 * 而两边的界面看起来都正常。所以交互控件（输入框 / 提交 / 报错 / 禁用判定）
 * 全部只实现一次（`LoginForm`），两个薄壳各自只负责一件事：
 * **调哪一侧的登录、成功后往哪走**。
 *
 * ### 后薄壳**不跳地址**
 *
 * `AdminLogin` 登录成功后**一句 navigate 都不写**：它由 `App.AdminRoute` 在
 * 「后台没登录」时渲染，会话一建立 `AdminRoute` 自己就换成后台正文，
 * 而地址栏本来就停在 `/admin`。顺手写个 `navigate('/admin')` 只会让人以为
 * 这里发生过一次跳转（其实没有，也不该有）。
 *
 * ### mock 提示
 *
 * mock 模式下把演示账号写出来（否则没人猜得到 `songshuai@hsy.com`）；
 * 连真后端时**不写**，避免把口令印在页面上 —— 真账号由管理员在 /admin 里发。
 * 两个变体各印**自己那一侧**的账号：会员变体印 `admin` 等于给一条注定被拒的路
 * （`admin` 现在住在 `admins` 表里，会员登录入口查不到它）。
 */
export function Login() {
  const { user, booting, login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // 来源页：路由 state（从别处 `<Navigate to="/login" state={{from}}>` 过来时带的）。
  // `RequireAuth` 就是这么传的，没有就回首页。
  const target = (location.state as { from?: string } | null)?.from || ''

  // 已经登着（含刷新后 booting 结束时的自动登录）→ 回到来源页或首页。
  // ⚠️ booting 期间不能直接放行，否则会先闪一下本页再跳走。
  if (!booting && user) {
    return <Navigate to={target || '/'} replace />
  }

  return (
    <LoginForm
      variant="member"
      onSubmit={async (account, password) => {
        await login(account, password)
        navigate(target || '/', { replace: true })
      }}
    />
  )
}

/**
 * 后台登录 —— `/admin` 未登录时**原地**渲染（不跳 `/login`）。
 *
 * 用户口径（2026-10-07）：「如果 `/admin` 的话，没登录就给个管理员登录页面」。
 *
 * 两条硬口径：
 * 1. **地址不跳走**。跳走再点回来是个死循环，且刷新即丢。
 * 2. **会员账号压根登不进来** —— 后台登录只查 `admins` 表（后端
 *    `admin_accounts.login`），会员账号在那一侧「不存在」。所以不存在
 *    「登着会员账号停在后台登录页」这种中间态，也就**不需要**
 *    「你不是管理员」这句提示：那句话在拆表前是为了解释一个真实存在的拒入，
 *    现在同样的输入得到的是「账号或密码不正确」—— 与别的错号一模一样，
 *    顺带把账号枚举口堵上了。
 */
export function AdminLogin() {
  const { login } = useAdminAuth()

  return (
    <LoginForm
      variant="admin"
      onSubmit={async (account, password) => {
        // 成功后不必 navigate：`AdminRoute` 会因为会话已建立而换成后台正文。
        await login(account, password)
      }}
    />
  )
}

/* -------------------------------------------------------------------------- */
/* 表单本体 —— 两个变体共用的一份实现                                          */
/* -------------------------------------------------------------------------- */

/** 两个变体各自的那几句文案。差别只有字，不是两套逻辑。 */
const COPY = {
  member: {
    icon: 'sparkles' as const,
    title: '华数云电商工作台',
    sub: '登录后所有任务、作品与额度都归到你的账号下',
    submit: '登录',
  },
  admin: {
    icon: 'shield' as const,
    title: '后台管理登录',
    sub: '后台是独立的账号体系 —— 与工作台各登各的，互不影响',
    submit: '登录后台',
  },
}

function LoginForm({
  variant,
  onSubmit,
}: {
  variant: 'member' | 'admin'
  /** 只负责「登录」这一件事；成功后往哪走由外面那层薄壳决定。 */
  onSubmit: (account: string, password: string) => Promise<void>
}) {
  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const text = COPY[variant]

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError('')
    try {
      await onSubmit(account.trim(), password)
    } catch (err) {
      // 后端对「账号不存在」与「密码错」返回同一句，这里原样照搬 ——
      // 前端再「友好化」成不同的提示，等于把后端特意堵上的账号枚举口又打开。
      setError(err instanceof ApiError ? err.message : '登录失败，请稍后重试')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth">
      <div className="auth__card" data-variant={variant === 'admin' ? 'admin' : undefined}>
        <div className="auth__brand">
          <span className="auth__logo" aria-hidden>
            <Icon name={text.icon} size={20} />
          </span>
          <div>
            <div className="auth__title">{text.title}</div>
            <div className="auth__sub">{text.sub}</div>
          </div>
        </div>

        <form className="auth__form" onSubmit={submit}>
          <Field label="账号">
            <Input
              value={account}
              autoFocus
              autoComplete="username"
              placeholder="邮箱或账号名"
              onChange={(e) => setAccount(e.target.value)}
            />
          </Field>
          <Field label="密码">
            <Input
              type="password"
              value={password}
              autoComplete="current-password"
              placeholder="••••••"
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>

          {error ? (
            <div className="auth__error" role="alert">
              <Icon name="alert" size={14} />
              <span>{error}</span>
            </div>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            icon="check"
            loading={busy}
            disabled={!account.trim() || !password}
          >
            {text.submit}
          </Button>
        </form>

        {API_MODE === 'mock' ? (
          <div className="auth__hint">
            <Badge tone="warning" icon="info">
              MOCK 数据
            </Badge>
            <div className="auth__hint-text">
              {variant === 'admin' ? (
                <>
                  原型演示模式（数据全在浏览器里）。后台账号：
                  <br />
                  <code>admin / admin12345</code>
                </>
              ) : (
                <>
                  原型演示模式（数据全在浏览器里）。可用账号：
                  <br />
                  <code>songshuai@hsy.com / 123456</code>（主账号，额度 20000）
                  <br />
                  <span className="muted">
                    后台账号（<code>admin</code>）是另一套体系，去 <code>/admin</code> 登录。
                  </span>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
